import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseSimpleMarkdown } from "@/utils/formatContent";
import { EntityMiniCard } from "@/components/EntityEmbed";
import SpeciesDetailModal from "@/components/marine/SpeciesDetailModal";
import DiveReportModal from "@/components/DiveReportModal";
import axios from "axios";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const ENTITY_REGEX = /(\[\[(species|report|trip|thread):([a-f0-9-]+)\]\])/g;

/**
 * Renders forum/thread content with both markdown formatting AND entity embeds.
 * Splits content on entity links, applies markdown to text parts, renders entity cards inline.
 */
const ForumRichContent = ({ content, images = [], className = "" }) => {
  const navigate = useNavigate();
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [speciesModalOpen, setSpeciesModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  if (!content) return null;

  const handleEntityClick = async (preview) => {
    if (preview.type === "species") {
      try {
        const res = await axios.get(`${API}/marine-database/${preview.id}`);
        setSelectedSpecies(res.data);
        setSpeciesModalOpen(true);
      } catch {
        navigate(`/community/marine-database?species=${preview.id}`);
      }
    } else if (preview.type === "report") {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const res = await axios.get(`${API}/dive-reports/${preview.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setSelectedReport(res.data);
        setReportModalOpen(true);
      } catch {
        navigate(`/community`);
      }
    } else if (preview.type === "trip") {
      navigate(`/community?tab=trips&trip=${preview.id}`);
    } else if (preview.type === "thread") {
      navigate(`/community/forum/${preview.id}`);
    }
  };

  // Split content into text and entity segments
  const parts = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(ENTITY_REGEX.source, "g");

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: content.slice(lastIndex, match.index) });
    }
    parts.push({ type: "entity", entityType: match[2], entityId: match[3] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) {
    parts.push({ type: "text", content: content.slice(lastIndex) });
  }

  const hasEntities = parts.some(p => p.type === "entity");

  // No entities — render pure markdown
  if (!hasEntities) {
    return (
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(content, images) }}
      />
    );
  }

  return (
    <>
      <div className={className}>
        {parts.map((part, idx) => {
          if (part.type === "text") {
            return (
              <span
                key={idx}
                dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(part.content, images) }}
              />
            );
          }
          return (
            <span key={idx} className="inline-block align-middle my-1">
              <EntityMiniCard
                type={part.entityType}
                id={part.entityId}
                onClick={handleEntityClick}
              />
            </span>
          );
        })}
      </div>

      {speciesModalOpen && selectedSpecies && (
        <SpeciesDetailModal
          species={selectedSpecies}
          onClose={() => {
            setSpeciesModalOpen(false);
            setSelectedSpecies(null);
          }}
          canEdit={false}
          onUpdate={() => {}}
        />
      )}

      {reportModalOpen && selectedReport && (
        <DiveReportModal
          report={selectedReport}
          isOpen={reportModalOpen}
          onClose={() => {
            setReportModalOpen(false);
            setSelectedReport(null);
          }}
        />
      )}
    </>
  );
};

export default ForumRichContent;
